import { Suspense, lazy, useEffect, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useTonAddress } from '@tonconnect/ui-react';
import { syncMe } from './lib/api';
import { ONBOARDING_DONE_EVENT, shouldBlockInitialDataLoad } from './lib/onboarding';
import { preloadMiniappTab, prewarmMiniappData, type MiniappTab } from './lib/prewarm';
import { trackTelegramEvent } from './lib/telegramAnalytics';
import { useAppStore } from './store';

const HomeRoute = lazy(() => import('./routes/Home').then((mod) => ({ default: mod.HomeRoute })));
const TokenRoute = lazy(() => import('./routes/Token').then((mod) => ({ default: mod.TokenRoute })));
const WalletRoute = lazy(() => import('./routes/Wallet').then((mod) => ({ default: mod.WalletRoute })));
const RoyaltiesRoute = lazy(() => import('./routes/Royalties').then((mod) => ({ default: mod.RoyaltiesRoute })));
const OnboardingSheet = lazy(() =>
  import('./components/OnboardingSheet').then((mod) => ({ default: mod.OnboardingSheet })),
);

function TabBar() {
  const location = useLocation();
  const walletAddress = useTonAddress();
  const tabs: Array<{ path: string; label: string; icon: string; preload: MiniappTab }> = [
    { path: '/', label: 'Tokens', icon: 'tokens', preload: 'tokens' },
    { path: '/royalties', label: 'Fees', icon: 'ton', preload: 'fees' },
    { path: '/wallet', label: 'Wallet', icon: 'wallet', preload: 'wallet' },
  ];
  const activeIndex = Math.max(0, tabs.findIndex((t) => location.pathname === t.path));
  const preloadTab = (tab: MiniappTab) => {
    if (shouldBlockInitialDataLoad()) return;
    preloadMiniappTab(tab, walletAddress || undefined);
  };
  return (
    <nav className="bottom-shell" data-active={activeIndex} aria-label="Primary navigation">
      {tabs.map((t) => {
        const active = location.pathname === t.path;
        return (
          <Link
            key={t.path}
            to={t.path}
            className={`bottom-tab${active ? ' active' : ''}`}
            aria-current={active ? 'page' : undefined}
            aria-label={t.label}
            onPointerEnter={() => preloadTab(t.preload)}
            onTouchStart={() => preloadTab(t.preload)}
            onClick={() => trackTelegramEvent('tab_click', { tab: t.icon })}
          >
            <span className={`bottom-tab-icon ${t.icon}`} aria-hidden="true" />
          </Link>
        );
      })}
    </nav>
  );
}

function TelegramReady() {
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    trackTelegramEvent('app_open', {
      platform: tg?.platform ?? 'unknown',
      startParam: tg?.initDataUnsafe?.start_param ?? '',
    });
    if (!tg) return;
    try {
      tg.ready();
      tg.expand();
      tg.disableVerticalSwipes?.();
    } catch (err) {
      console.warn('Telegram WebApp init issue', err);
    }
  }, []);
  return null;
}

// CC-076 — sync user to backend on mount. This is what triggers
// upsertUser (writes users.username) and CC-070 auto-link of pending
// claim earmarks. Without this call, pending @username deploys can
// never settle to the recipient's tg_id.
function MeSync() {
  const setMe = useAppStore((s) => s.setMe);
  const setAuthError = useAppStore((s) => s.setAuthError);
  useEffect(() => {
    syncMe({})
      .then(setMe)
      .catch((err: unknown) => {
        console.warn('postMe failed', err);
        setAuthError(err instanceof Error ? err.message : 'postMe failed');
      });
  }, [setAuthError, setMe]);
  return null;
}

// Deep-link router: bot crafts URLs like t.me/<bot>?startapp=royalties or
// t.me/<bot>?startapp=token_<address>. The mini-app picks the start_param up
// from initDataUnsafe and navigates accordingly. Only fires once on mount.
function StartParamRouter() {
  const navigate = useNavigate();
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    const param = tg?.initDataUnsafe?.start_param;
    if (!param) return;
    if (param === 'royalties') {
      navigate('/royalties', { replace: true });
    } else if (param.startsWith('token_')) {
      const addr = param.slice('token_'.length);
      if (addr) navigate(`/token/${encodeURIComponent(addr)}`, { replace: true });
    }
    // We intentionally don't run this on every navigation — only the initial
    // landing. Otherwise re-renders would fight any user-driven navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function RouteAnalytics() {
  const location = useLocation();
  useEffect(() => {
    trackTelegramEvent('screen_view', { path: location.pathname });
  }, [location.pathname]);
  return null;
}

function DataPrewarm() {
  const walletAddress = useTonAddress();
  useEffect(() => {
    let cancelPrewarm: (() => void) | undefined;
    const start = () => {
      cancelPrewarm?.();
      cancelPrewarm = prewarmMiniappData(walletAddress || undefined);
    };

    if (!shouldBlockInitialDataLoad()) {
      start();
      return () => cancelPrewarm?.();
    }

    const onDone = () => start();
    window.addEventListener(ONBOARDING_DONE_EVENT, onDone, { once: true });
    return () => {
      window.removeEventListener(ONBOARDING_DONE_EVENT, onDone);
      cancelPrewarm?.();
    };
  }, [walletAddress]);
  return null;
}

function useOnboardingGate() {
  const [mainReady, setMainReady] = useState(() => !shouldBlockInitialDataLoad());

  useEffect(() => {
    if (mainReady) return undefined;
    const onDone = () => setMainReady(true);
    window.addEventListener(ONBOARDING_DONE_EVENT, onDone, { once: true });
    return () => window.removeEventListener(ONBOARDING_DONE_EVENT, onDone);
  }, [mainReady]);

  return mainReady;
}

export function App() {
  const mainReady = useOnboardingGate();

  return (
    <BrowserRouter>
      <TelegramReady />
      <StartParamRouter />
      {mainReady && (
        <>
          <MeSync />
          <RouteAnalytics />
          <DataPrewarm />
        </>
      )}

      <div className={`app-shell${mainReady ? '' : ' onboarding-only'}`}>
        <main className="app-main">
          {mainReady ? (
            <Suspense fallback={<div className="container"><div className="skeleton" style={{ height: 120 }} /></div>}>
              <Routes>
                <Route path="/" element={<HomeRoute />} />
                <Route path="/token/:masterAddr" element={<TokenRoute />} />
                <Route path="/royalties" element={<RoyaltiesRoute />} />
                <Route path="/wallet" element={<WalletRoute />} />
              </Routes>
            </Suspense>
          ) : (
            <div className="onboarding-calm-backdrop" aria-hidden="true" />
          )}
        </main>
        {mainReady && <TabBar />}
        <Suspense fallback={null}>
          <OnboardingSheet />
        </Suspense>
      </div>
    </BrowserRouter>
  );
}
